import api from './api';
import tenantService from './tenantService';
import memberService from './memberService';
import { getValidTenantId, TENANT_ID_STORAGE_KEY } from '../utils/tenantUtils';

const ENDPOINT = '/analytics';

const analyticsService = {
  async getDashboardAnalytics() {
    try {
      console.log('Fetching dashboard analytics');
      
      // Use the standardized tenant ID function
      const tenantId = getValidTenantId();
      
      if (!tenantId) {
        console.warn('No tenant ID available for dashboard analytics');
      } else {
        console.log(`Using tenant ID for dashboard analytics: ${tenantId}`);
      }

      // Let the API interceptor handle adding the tenant ID to query params
      const response = await api.get(`${ENDPOINT}/dashboard`);
      
      console.log('Dashboard analytics response:', {
        success: response.data?.success,
        hasData: !!response.data?.data
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      
      // Try to get data from memberService as a fallback
      try {
        console.log('Attempting to fetch member stats as fallback');
        const memberStats = await memberService.getDashboardStats();
        if (memberStats) {
          console.log('Successfully retrieved member stats as fallback');
          return { 
            success: true, 
            data: this.transformMemberStatsToAnalytics(memberStats),
            source: 'memberService' 
          };
        }
      } catch (memberError) {
        console.error('Fallback to memberService also failed:', memberError);
      }
      
      // If both primary and fallback fail, use default data
      const defaultData = this.getDefaultAnalytics();
      return { success: false, data: defaultData };
    }
  },

  async getMembershipAnalytics(timeframe = '1y') {
    try {
      console.log(`Fetching membership analytics for timeframe: ${timeframe}`);
      
      // First try to get member stats from memberService
      try {
        const memberStats = await memberService.getDashboardStats();
        if (memberStats) {
          console.log('Using member stats for membership analytics');
          const transformedData = this.transformMemberStatsToAnalytics(memberStats);
          
          // Only return early if we have the data we need for membership analytics
          if (transformedData && transformedData.memberGrowth) {
            return { 
              success: true, 
              data: {
                growth: transformedData.memberGrowth,
                distribution: transformedData.membershipDistribution || [],
                retention: transformedData.retention || 0
              },
              source: 'memberService'
            };
          }
        }
      } catch (memberError) {
        // Log but continue to fallback
        console.warn('Could not fetch member stats, falling back to analytics API:', memberError);
      }
      
      // Fallback to regular analytics API
      // Use the standardized tenant ID function
      const tenantId = getValidTenantId();
      
      if (!tenantId) {
        console.warn('No tenant ID available for membership analytics');
      }

      // Let the API interceptor handle adding the tenant ID
      const response = await api.get(`${ENDPOINT}/membership`, {
        params: { timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching membership analytics:', error);
      return { success: false, data: { growth: [], distribution: [], retention: 0 } };
    }
  },

  async getFinancialAnalytics(timeframe = '1y') {
    try {
      console.log(`Fetching financial analytics for timeframe: ${timeframe}`);
      
      // Use the standardized tenant ID function
      const tenantId = getValidTenantId();

      if (!tenantId) {
        console.warn('No tenant ID available for financial analytics');
      }

      // Let the API interceptor handle adding the tenant ID
      const response = await api.get(`${ENDPOINT}/financial`, {
        params: { timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching financial analytics:', error);
      return { success: false, data: { revenue: [], transactions: [], summary: {} } };
    }
  },

  async getEngagementAnalytics() {
    try {
      console.log('Fetching engagement analytics');
      
      // Use the standardized tenant ID function
      const tenantId = getValidTenantId();
      
      if (!tenantId) {
        console.warn('No tenant ID available for engagement analytics');
      }
      
      // Let the API interceptor handle adding the tenant ID
      const response = await api.get(`${ENDPOINT}/engagement`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement analytics:', error);
      return { success: false, data: { activity: [], satisfaction: 0, nps: 0 } };
    }
  },

  async getCustomerExperienceAnalytics(timeframe = '1y') {
    try {
      console.log(`Fetching customer experience analytics for timeframe: ${timeframe}`);
      
      // Use the standardized tenant ID function
      const tenantId = getValidTenantId();
      
      if (!tenantId) {
        console.warn('No tenant ID available for customer experience analytics');
      }

      // Let the API interceptor handle adding the tenant ID
      const response = await api.get(`${ENDPOINT}/customer-experience`, {
        params: { timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching customer experience analytics:', error);
      return { success: false, data: this.getDefaultCustomerExperienceData() };
    }
  },

  // Helper method to transform member stats into analytics format
  transformMemberStatsToAnalytics(memberStats) {
    if (!memberStats) return null;
    
    try {
      const result = {};
      
      // Transform member growth data if available
      if (memberStats.membershipTrends) {
        result.memberGrowth = memberStats.membershipTrends.map(item => ({
          month: item.period,
          members: item.count
        }));
      }
      
      // Transform membership type distribution if available
      if (memberStats.membershipTypes) {
        result.membershipDistribution = memberStats.membershipTypes.map(item => ({
          name: item.type,
          value: item.count
        }));
      }
      
      // Transform financial data if available
      if (memberStats.revenueData || memberStats.financialSummary) {
        result.financialMetrics = {
          totalRevenue: memberStats.financialSummary?.totalRevenue || 0,
          revenueGrowth: memberStats.financialSummary?.growthRate || 0,
          averageRevenue: memberStats.financialSummary?.averageTransaction || 0,
          revenueByMonth: memberStats.revenueData?.map(item => ({
            month: item.period,
            revenue: item.amount
          })) || []
        };
      }
      
      // Transform engagement data if available
      if (memberStats.engagementMetrics) {
        result.customerSuccess = {
          npsScore: memberStats.engagementMetrics.npsScore || 0,
          satisfactionRate: memberStats.engagementMetrics.satisfactionRate || 0,
          engagementLevels: memberStats.engagementMetrics.levels?.map(item => ({
            name: item.level,
            value: item.percentage
          })) || []
        };
      }
      
      // Transform churn data if available
      if (memberStats.churn) {
        result.churnAnalysis = {
          rate: memberStats.churn.rate || 0,
          reasons: memberStats.churn.reasons?.map(item => ({
            name: item.reason,
            value: item.percentage
          })) || []
        };
        // Add retention rate based on churn
        result.retention = 100 - (memberStats.churn.rate || 0);
      }
      
      return result;
    } catch (error) {
      console.error('Error transforming member stats to analytics format:', error);
      return null;
    }
  },

  getDefaultAnalytics() {
    return {
      memberGrowth: Array.from({ length: 6 }, (_, i) => ({
        month: new Date(Date.now() - (5-i) * 30 * 24 * 60 * 60 * 1000)
          .toLocaleString('default', { month: 'short' }),
        members: Math.floor(Math.random() * 50) + 50
      })),
      financialMetrics: {
        totalRevenue: 52840,
        revenueGrowth: 8.5,
        averageRevenue: 245.50,
        revenueByMonth: Array.from({ length: 6 }, (_, i) => ({
          month: new Date(Date.now() - (5-i) * 30 * 24 * 60 * 60 * 1000)
            .toLocaleString('default', { month: 'short' }),
          revenue: Math.floor(Math.random() * 1000) + 3000
        }))
      },
      customerSuccess: {
        npsScore: 72,
        satisfactionRate: 92.3,
        engagementLevels: [
          { name: 'High', value: 45 },
          { name: 'Medium', value: 35 },
          { name: 'Low', value: 20 }
        ]
      },
      churnAnalysis: {
        rate: 4.2,
        reasons: [
          { name: 'Price', value: 40 },
          { name: 'Features', value: 25 },
          { name: 'Support', value: 20 },
          { name: 'Competition', value: 15 }
        ]
      }
    };
  },

  getDefaultCustomerExperienceData() {
    return {
      satisfaction: {
        overall: 87.5,
        trends: Array.from({ length: 6 }, (_, i) => ({
          month: new Date(Date.now() - (5-i) * 30 * 24 * 60 * 60 * 1000)
            .toLocaleString('default', { month: 'short' }),
          score: 80 + Math.random() * 10
        })),
        categories: [
          { name: 'Support', value: 89 },
          { name: 'Usability', value: 85 },
          { name: 'Features', value: 78 },
          { name: 'Value', value: 82 },
          { name: 'Overall', value: 83 }
        ]
      },
      nps: {
        score: 72,
        breakdown: [
          { name: 'Promoters', value: 70 },
          { name: 'Passive', value: 20 },
          { name: 'Detractors', value: 10 }
        ],
        trends: Array.from({ length: 6 }, (_, i) => ({
          month: new Date(Date.now() - (5-i) * 30 * 24 * 60 * 60 * 1000)
            .toLocaleString('default', { month: 'short' }),
          score: 65 + Math.random() * 15
        }))
      },
      feedback: {
        sentimentAnalysis: [
          { name: 'Positive', value: 65 },
          { name: 'Neutral', value: 25 },
          { name: 'Negative', value: 10 }
        ],
        commonTopics: [
          { name: 'Customer Support', mentions: 45, sentiment: 4.2 },
          { name: 'User Interface', mentions: 38, sentiment: 3.8 },
          { name: 'Performance', mentions: 32, sentiment: 3.2 },
          { name: 'Features', mentions: 28, sentiment: 4.5 },
          { name: 'Pricing', mentions: 25, sentiment: 3.0 }
        ]
      }
    };
  }
};

export default analyticsService;
export { analyticsService };
